import React from 'react';
import { DashboardData } from '../types';

interface SettingsPanelProps {
  data: DashboardData;
  onInputChange: (
    field: keyof DashboardData,
    value: string | number | boolean,
    inputType?: 'slider' | 'input' | 'select'
  ) => void;
  onSliderChangeEnd?: (field: keyof DashboardData, value: number) => void;
  onCheckboxChange: (field: string, checked: boolean) => void;
  onAnalysisClick?: () => void;
  isAnalysisEnabled?: boolean;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({
  data,
  onInputChange,
  onSliderChangeEnd,
  onCheckboxChange,
  onAnalysisClick,
  isAnalysisEnabled = true,
}) => {
  return (
    <div className="lg:col-span-1">
      <div className="dashboard-card p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">設定パネル</h2>

        {/* 採用計画設定 */}
        <div className="settings-section">
          <div className="settings-title">📋 採用計画設定</div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                総合職採用目標人数
              </label>
              <input
                type="range"
                className="slider w-full"
                min="1"
                max="100"
                value={data.generalHiring}
                onChange={e =>
                  onInputChange(
                    'generalHiring',
                    parseInt(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'generalHiring',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'generalHiring',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">{data.generalHiring}人</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                技術職採用目標人数
              </label>
              <input
                type="range"
                className="slider w-full"
                min="1"
                max="100"
                value={data.techHiring}
                onChange={e =>
                  onInputChange(
                    'techHiring',
                    parseInt(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'techHiring',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'techHiring',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">{data.techHiring}人</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                採用予算
              </label>
              <input
                type="range"
                className="slider w-full"
                min="100"
                max="2000"
                value={data.budget}
                onChange={e =>
                  onInputChange('budget', parseInt(e.target.value), 'slider')
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'budget',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'budget',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">{data.budget}万円</div>
            </div>
          </div>
        </div>

        {/* 企業規模設定 */}
        <div className="settings-section">
          <div className="settings-title">🏢 企業規模設定</div>
          <select
            value={data.companySize}
            onChange={e => {
              // console.log(
              //   'Company size changed:',
              //   e.target.value,
              //   'Current data.companySize:',
              //   data.companySize
              // );
              onInputChange(
                'companySize',
                parseFloat(e.target.value),
                'select'
              );
            }}
          >
            <option value={1.0}>スタートアップ（~100人）</option>
            <option value={0.9}>中堅スタートアップ（101~299人）</option>
            <option value={0.8}>中小企業（300~999人）</option>
            <option value={0.7}>中堅企業（1000~4999人）</option>
            <option value={0.6}>大企業（5000人以上）</option>
          </select>
        </div>

        {/* 業界分類 */}
        <div className="settings-section">
          <div className="settings-title">🏭 業界分類</div>
          <select
            value={data.industry}
            onChange={e =>
              onInputChange('industry', parseFloat(e.target.value), 'select')
            }
          >
            <option value={1.2}>IT・通信業界</option>
            <option value={1.1}>金融業界</option>
            <option value={0.9}>製造業</option>
            <option value={0.8}>小売業</option>
            <option value={1.5}>コンサルティング</option>
          </select>
        </div>

        {/* 競合分析係数 */}
        <div className="settings-section">
          <div className="settings-title">🏆 競合分析係数</div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                初任給競争力
              </label>
              <input
                type="range"
                className="slider w-full"
                min="20"
                max="35"
                step="0.1"
                value={data.salary}
                onChange={e =>
                  onInputChange('salary', parseFloat(e.target.value), 'slider')
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'salary',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'salary',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">{data.salary.toFixed(1)}万円</div>
              <div className="text-xs text-gray-500 mt-1">
                全国平均: 22.6万円
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                事業安定性係数
              </label>
              <input
                type="range"
                className="slider w-full"
                min="1"
                max="5"
                step="0.1"
                value={data.stability}
                onChange={e =>
                  onInputChange(
                    'stability',
                    parseFloat(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'stability',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'stability',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">
                {data.stability.toFixed(1)} -{' '}
                {data.stability >= 4.5
                  ? '非常に安定'
                  : data.stability >= 4.0
                    ? '高安定'
                    : data.stability >= 3.0
                      ? '普通'
                      : data.stability >= 2.0
                        ? 'やや不安定'
                        : '不安定'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                人材成長性係数
              </label>
              <input
                type="range"
                className="slider w-full"
                min="1"
                max="5"
                step="0.1"
                value={data.growth}
                onChange={e =>
                  onInputChange('growth', parseFloat(e.target.value), 'slider')
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'growth',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'growth',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">
                {data.growth.toFixed(1)} -{' '}
                {data.growth >= 4.5
                  ? '非常に高成長'
                  : data.growth >= 4.0
                    ? '高成長期待'
                    : data.growth >= 3.0
                      ? '普通'
                      : data.growth >= 2.0
                        ? 'やや成長機会少'
                        : '成長機会少'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                働きやすさ
              </label>
              <input
                type="range"
                className="slider w-full"
                min="1"
                max="5"
                step="0.1"
                value={data.workLifeBalance}
                onChange={e =>
                  onInputChange(
                    'workLifeBalance',
                    parseFloat(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'workLifeBalance',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'workLifeBalance',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">
                {data.workLifeBalance.toFixed(1)} -{' '}
                {data.workLifeBalance >= 4.5
                  ? 'とても働きやすい'
                  : data.workLifeBalance >= 4.0
                    ? '働きやすい'
                    : data.workLifeBalance >= 3.0
                      ? '普通'
                      : data.workLifeBalance >= 2.0
                        ? 'やや働きにくい'
                        : '働きにくい'}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                会社の技術力
              </label>
              <input
                type="range"
                className="slider w-full"
                min="1"
                max="5"
                step="0.1"
                value={data.technicalCapability}
                onChange={e =>
                  onInputChange(
                    'technicalCapability',
                    parseFloat(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'technicalCapability',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'technicalCapability',
                    parseFloat((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">
                {data.technicalCapability.toFixed(1)} -{' '}
                {data.technicalCapability >= 4.5
                  ? '業界をリードする技術力'
                  : data.technicalCapability >= 4.0
                    ? '高い技術力'
                    : data.technicalCapability >= 3.0
                      ? '標準的な技術力'
                      : data.technicalCapability >= 2.0
                        ? '技術面で課題あり'
                        : '技術力が低い'}
              </div>
            </div>
          </div>
        </div>

        {/* ブランドスコア評価 */}
        <div className="settings-section">
          <div className="settings-title">🌟 ブランドスコア評価</div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                オーガニック応募比率
              </label>
              <input
                type="range"
                className="slider w-full"
                min="0"
                max="100"
                value={data.organicRatio}
                onChange={e =>
                  onInputChange(
                    'organicRatio',
                    parseInt(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'organicRatio',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'organicRatio',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">{data.organicRatio}%</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                SNSフォロワー数
              </label>
              <input
                type="range"
                className="slider w-full"
                min="0"
                max="200000"
                step="100"
                value={data.snsFollowers}
                onChange={e =>
                  onInputChange(
                    'snsFollowers',
                    parseInt(e.target.value),
                    'slider'
                  )
                }
                onMouseUp={e =>
                  onSliderChangeEnd?.(
                    'snsFollowers',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
                onTouchEnd={e =>
                  onSliderChangeEnd?.(
                    'snsFollowers',
                    parseInt((e.target as HTMLInputElement).value)
                  )
                }
              />
              <div className="value-display">
                {data.snsFollowers.toLocaleString()}人
              </div>
              <div className="text-xs text-gray-500 mt-1">
                最大20万人以上対応
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                会社ページ月間PV
              </label>
              <input
                type="number"
                value={data.pagePV}
                onChange={e =>
                  onInputChange('pagePV', parseInt(e.target.value))
                }
              />
              <div className="text-xs text-gray-500 mt-1">PV</div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                口コミサイト評価
              </label>
              <select
                value={data.rating}
                onChange={e => {
                  // console.log(
                  //   'Rating changed:',
                  //   e.target.value,
                  //   'Current data.rating:',
                  //   data.rating
                  // );
                  onInputChange('rating', parseFloat(e.target.value), 'select');
                }}
              >
                <option value={1.0}>★1.0</option>
                <option value={2.0}>★2.0</option>
                <option value={3.0}>★3.0</option>
                <option value={3.8}>★3.8</option>
                <option value={4.2}>★4.2</option>
                <option value={4.5}>★4.5</option>
                <option value={5.0}>★5.0</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                PR施策実施状況
              </label>
              <div className="checkbox-group">
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="employeeRef"
                    defaultChecked
                    onChange={e =>
                      onCheckboxChange('prMeasures', e.target.checked)
                    }
                  />
                  <label htmlFor="employeeRef">社員紹介制度</label>
                </div>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="prVideo"
                    defaultChecked
                    onChange={e =>
                      onCheckboxChange('prMeasures', e.target.checked)
                    }
                  />
                  <label htmlFor="prVideo">PR動画制作</label>
                </div>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="snsActive"
                    defaultChecked
                    onChange={e =>
                      onCheckboxChange('prMeasures', e.target.checked)
                    }
                  />
                  <label htmlFor="snsActive">SNS積極活用</label>
                </div>
                <div className="checkbox-item">
                  <input
                    type="checkbox"
                    id="jobFair"
                    defaultChecked
                    onChange={e =>
                      onCheckboxChange('prMeasures', e.target.checked)
                    }
                  />
                  <label htmlFor="jobFair">合同説明会参加</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 地域・業界設定 */}
        <div className="settings-section">
          <div className="settings-title">🌍 地域・業界設定</div>
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              対象地域
            </label>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="tokyo"
                  defaultChecked
                  onChange={e =>
                    onCheckboxChange('regionalFactor', e.target.checked)
                  }
                />
                <label htmlFor="tokyo">東京都</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="osaka"
                  onChange={e =>
                    onCheckboxChange('regionalFactor', e.target.checked)
                  }
                />
                <label htmlFor="osaka">大阪府</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="aichi"
                  onChange={e =>
                    onCheckboxChange('regionalFactor', e.target.checked)
                  }
                />
                <label htmlFor="aichi">愛知県</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="kanagawa"
                  onChange={e =>
                    onCheckboxChange('regionalFactor', e.target.checked)
                  }
                />
                <label htmlFor="kanagawa">神奈川県</label>
              </div>
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="fukuoka"
                  onChange={e =>
                    onCheckboxChange('regionalFactor', e.target.checked)
                  }
                />
                <label htmlFor="fukuoka">福岡県</label>
              </div>
            </div>
          </div>
        </div>

        {/* 分析実行ボタン */}
        {isAnalysisEnabled && (
          <div className="mt-8">
            <button
              onClick={onAnalysisClick}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-6 px-4 rounded-lg transition-colors shadow-lg"
              style={{ minHeight: '4.5rem' }}
            >
              分析実行
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPanel;
